import { Button, Icon, Text, type ButtonProps } from "@salestrak/ui";

/**
 * The start affordance a surface opts into when no follow-up is open — a
 * quiet muted-pulse "Follow up" button. Kept beside {@link
 * UpdateFollowUpButton} so the two states of the same slot can't drift apart
 * visually.
 *
 * `iconOnly` is the detail header's rung (Spencer, 2026-09-10): starting is
 * not the one thing a rep must do on the surface, so the tab strip carries a
 * ghost pulse glyph named by its tooltip, not a filled verb. The label
 * becomes the accessible name; the host owns the tooltip.
 *
 * Spreads the rest of its ButtonProps so it works under a Base UI `render=`
 * trigger (the detail header's tooltip): the trigger merges its own
 * onClick/ref/aria into this element's props, and the spread carries them to
 * the Button.
 */
export function StartFollowUpButton({
  label = "Start Follow up",
  size = "xs",
  variant = "ghost",
  iconOnly = false,
  ...rest
}: { label?: string; iconOnly?: boolean } & ButtonProps) {
  if (iconOnly) {
    return (
      <Button aria-label={label} size="icon-xs" variant="ghost" {...rest}>
        <Icon name="heart-pulse" size="sm" color="muted" />
      </Button>
    );
  }
  // Quiet hosts (ghost, secondary) mute the words and keep the 14px label
  // their rows read at. The verb rungs — the detail header's filled primary
  // and the Follow up section's outline shortcut — keep their own ink (muted
  // words on a primary fill read as disabled) and inherit the button rung's
  // type and glyph, the toolbar create action's grammar, so "Start Follow up"
  // and "Add Project" are the same control.
  const quiet = variant !== "solid" && variant !== "outline";
  return (
    <Button size={size} variant={variant} {...rest}>
      <Icon
        name="heart-pulse"
        size={quiet ? "sm" : "xs"}
        color={quiet ? "muted" : undefined}
      />
      <Text size={quiet ? "sm" : null} variant={quiet ? "muted" : undefined}>
        {label}
      </Text>
    </Button>
  );
}

/**
 * The other state of the detail header's verb slot: while a follow-up is open
 * (or a Project's update clock is running) the slot posts an update. Primary,
 * because it is the one thing the rep must do on the surface and nothing else
 * in the header is filled; the Follow up section's hover shortcut passes
 * `outline`. `xs` with inherited type — the toolbar create action's rung
 * ("Add Project", "Add Account"), so the two primary verbs a rep meets on
 * these screens are one size. Kept beside {@link StartFollowUpButton} so the
 * slot's two states share one file.
 *
 * Spreads its ButtonProps for the same reason the start button does — the
 * host may seat it under a `render=` trigger.
 */
export function UpdateFollowUpButton({ size = "xs", ...rest }: ButtonProps) {
  return (
    <Button size={size} {...rest}>
      Update
    </Button>
  );
}
